/** @file Dialog in a portal: modal (focus trapped, backdrop closes it) or a non-modal side sheet. */
import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './tokens.css';
import './Dialog.css';
import { useDialogFocus } from './useDialogFocus';
import { useRestoreFocus } from './useRestoreFocus';

/** Props of Dialog. */
export interface DialogProps {
  open: boolean;
  /** Already-translated title; also the dialog's accessible name. */
  title: string;
  /** Already-translated accessible name of the close button. */
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  /** Action buttons row (for example Button elements); omit for none. */
  footer?: ReactNode;
  /** Default true. False: no dimming and no blocking backdrop (the page stays usable), docked right; Escape and the buttons close it. */
  modal?: boolean;
}

/** Renders nothing while closed; on close, focus returns to the element that opened it. A non-modal one passes every key but Escape through. */
export function Dialog({ open, title, closeLabel, onClose, children, footer, modal = true }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useRestoreFocus(open);
  const trap = useDialogFocus(ref, open, onClose);
  const onKeyDown = modal ? trap : (e: KeyboardEvent<HTMLElement>): void => {
    if (e.code !== 'Escape' || e.nativeEvent.isComposing) return;
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };
  if (!open) return null;
  return createPortal(
    <div className={modal ? 'p2-dialog-backdrop' : 'p2-dialog-backdrop p2-dialog-nonmodal'} onPointerDown={modal ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}>
      <div ref={ref} role="dialog" aria-modal={modal ? 'true' : 'false'} aria-labelledby={titleId} tabIndex={-1} className="p2-dialog" onKeyDown={onKeyDown}>
        <header className="p2-dialog-header">
          <h2 id={titleId} className="p2-dialog-title">{title}</h2>
          <button type="button" className="p2-dialog-close" aria-label={closeLabel} onClick={onClose}>{'×'}</button>
        </header>
        <div className="p2-dialog-body">{children}</div>
        {footer !== undefined && <footer className="p2-dialog-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
